/* eslint-disable no-unused-vars */
import { reactive } from "@vue/reactivity";
import LZString from "lz-string";

import {
  // NAVIGATE_TO_SEARCH_PAGE,
  CHECK_FILTER_MODELS_UPDATED,
  CHECK_FILTER_MODELS_DEFAULTED,
  SET_STATE_FROM_QUERY_PARAM,
  SET_LOADING,
  SET_LOADED,
  SET_PAGE,
  SET_PAGINATION_TOTALS,
  SET_SORT,
  SET_URL_PARAMS,
  SET_LOCATION,
  SET_FILTER_OPTIONS,
  SET_FEATURES_FILTER_OPTIONS_LOADING,
  POPULATE_FILTER_OPTIONS,
  APPLY_FILTER_MODELS,
  RESET_PAGINATION,
  SET_SEARCH_FILTERS,
  ADD_SEARCH_FILTER,
  REMOVE_SEARCH_FILTER,
  UPDATE_SEARCH_FILTER,
  SET_SEARCH_OPTIONS,
  SET_SEARCH_SORTING,
  SET_SEARCH_SCORING,
  ADD_SEARCH_SCORING,
  REMOVE_SEARCH_SCORING,
  UPDATE_SEARCH_SCORING,
  SYNC_STANDARDIZED_STATE,
  REMOVE_COMPLEX_FILTERS,
  REQUEST_NEW_VISITOR_SEARCH_SCORING
} from "@/store/actions/search";
import mixins from "@/mixins";
import axios from "axios";

// Default model structure template (used for extracting filters)
const getDefaultModelStructure = () => ({
  bodyType: [],
  makeId: [],
  modelId: [],
  year: { min: null, max: null },
  price: { min: null, max: null },
  mileage: { min: null, max: null },
  exteriorColor: [],
  interiorColor: [],
  condition: [],
  fuelType: [],
  dealScorePhrase: [],
  transmission: [],
  engineType: [],
  drivetrain: [],
  trim: [],
  "features.fogLights": "",
  "features.autopilot": "",
  "features.tractionControl": "",
  "features.keylessEntry": "",
  "features.sunshades": "",
  "features.backupCamera": "",
  "features.blindSpotMonitor": "",
  "features.stabilityControl": "",
  "features.cruiseControl": "",
  "features.automaticParking": "",
  "features.bluetoothConnectivity": "",
  "features.driverAttentionWarning": "",
  "features.offroadDrivingAssist": "",
  "features.appleCarplay": "",
  "features.androidAuto": "",
  "features.navigationSystem": "",
  "features.headsUpDisplay": "",
  "features.adaptiveCruiseControl": "",
  "features.trailerHitch": "",
  "features.360DegreeCamera": "",
  "features.collisionWarning": "",
  "features.touchscreenDisplay": "",
  "features.ventilatedSeats": "",
  "features.adaptiveHeadlights": "",
  "features.roofRails": "",
  "features.dashCamera": "",
  "features.sunroofMoonroof": "",
  "features.laneKeepAssist": "",
  "features.powerTrunk": "",
  "features.climateControl": "",
  "features.crossTrafficAlert": "",
  "features.automaticEmergencyBraking": "",
  "features.trafficSignRecognition": "",
  "features.homelink": "",
  "features.rainSensingWipers": "",
  "features.voiceControls": "",
  "features.antiTheftSensors": "",
  "features.powerAdjustableSeats": "",
  "features.heatedMirrors": "",
  "features.memorySeats": "",
  "features.pushButtonStart": "",
  "features.powerFoldingMirrors": "",
  "features.heatedSteeringWheel": "",
  "features.illuminatedEntry": "",
  "features.brakeAssist": "",
  "features.cdPlayer": "",
  "features.usbInputs": "",
  "features.remoteStart": "",
  "features.laneDepartureWarning": "",
  "features.premiumWheels": "",
  "features.parkingSensors": "",
  "features.premiumSoundSystem": "",
  "features.leatherSeats": "",
  "features.ledHeadlights": "",
  "features.memoryMirrors": "",
  "features.satelliteRadio": "",
  "features.digitalOdometer": "",
  "features.wifiHotspot": "",
  "features.handsFreeTrunk": "",
  "features.wirelessPhoneCharging": "",
  "features.handsFreePhone": "",
  "features.thirdRowSeating": "",
  "features.heatedSeats": ""
});

const state = reactive({
  loaded: false,
  featuresFilterOptionsLoading: false,
  pendingScoringRequests: [],
  searchMarketAverages: {
    dealScore: null,
    price: null,
    mileage: null,
    year: null
  },
  // Primary state: search objects
  searchFilters: [],
  searchFiltersHistory: (() => {
    try {
      const stored = window.localStorage.getItem(
        "carscience-visitor-search-filter-history"
      );
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  })(),
  searchOptions: {
    location_search: {
      postal_code:
        window.localStorage.getItem(
          window.location.hostname + "-searchLocationPostalCode"
        ) || "",
      radius:
        window.localStorage.getItem(
          window.location.hostname + "-searchLocationRadius"
        ) || 100,
      include_distance_field: true
    },
    deduplicate_fields: ["vin"],
    faceting: {
      fields: {
        condition: [],
        make_id: [],
        model_id: [],
        body_type: [],
        exterior_color: [],
        interior_color: [],
        fuel_type: [],
        engine_type: [],
        deal_score_phrase: [],
        deal_score: ["range"],
        transmission: [],
        price: ["range"],
        mileage: ["range"],
        year: ["range"],
        drivetrain: []
      },
      size: 2000
    }
  },
  searchSorting: [{ _score: "desc" }],
  searchScoring: (() => {
    try {
      const stored = window.localStorage.getItem(
        "carscience-visitor-profile-scoring"
      );
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  })(),
  // Complex/unhandled filter clauses (preserved but not editable in UI)
  searchFiltersComplex: [],
  // Derived/cached location data for UI
  location: {
    radius: window.localStorage.getItem(
      window.location.hostname + "-searchLocationRadius"
    )
      ? window.localStorage.getItem(
          window.location.hostname + "-searchLocationRadius"
        )
      : 100,
    postalCode: window.localStorage.getItem(
      window.location.hostname + "-searchLocationPostalCode"
    )
      ? window.localStorage.getItem(
          window.location.hostname + "-searchLocationPostalCode"
        )
      : "",
    city: window.localStorage.getItem(
      window.location.hostname + "-searchLocationCity"
    )
      ? window.localStorage.getItem(
          window.location.hostname + "-searchLocationCity"
        )
      : "",
    state: window.localStorage.getItem(
      window.location.hostname + "-searchLocationState"
    )
      ? window.localStorage.getItem(
          window.location.hostname + "-searchLocationState"
        )
      : ""
  },
  pagination: {
    currentPage: 1,
    pageSelectModel: 1,
    totalResults: 0,
    totalPages: 500,
    perPage: 20,
    disableAll: false
    // Note: disableNext and disablePrevious are now computed getters
  },
  sort: {
    model: "advanced.recommended",
    options: {
      classic: [
        {
          label: "Highest Mileage",
          value: "classic.highest_mileage",
          searchObjects: [{ mileage: "desc", _score: "desc" }]
        },
        {
          label: "Lowest Mileage",
          value: "classic.lowest_mileage",
          searchObjects: [{ mileage: "asc", _score: "desc" }]
        },
        {
          label: "Highest Price",
          value: "classic.highest_price",
          searchObjects: [{ price: "desc", _score: "desc" }]
        },
        {
          label: "Lowest Price",
          value: "classic.lowest_price",
          searchObjects: [{ price: "asc", _score: "desc" }]
        },
        {
          label: "Newest Year",
          value: "classic.newest_year",
          searchObjects: [{ year: "desc", _score: "desc" }]
        },
        {
          label: "Oldest Year",
          value: "classic.oldest_year",
          searchObjects: [{ year: "asc", _score: "desc" }]
        },
        {
          label: "Newest Listed",
          value: "classic.newest_listed",
          searchObjects: [{ id: "desc", _score: "desc" }]
        },
        {
          label: "Oldest Listed",
          value: "classic.oldest_listed",
          searchObjects: [{ id: "asc", _score: "desc" }]
        },
        {
          label: "Nearest Location",
          value: "classic.nearest_location",
          searchObjects: [{ distance: "asc", _score: "desc" }]
        }
      ],
      advanced: [
        {
          label: "Best Matches For You",
          value: "advanced.recommended",
          searchObjects: [{ _score: "desc" }]
        },
        {
          label: "Best Deal Score",
          value: "advanced.deal_score",
          searchObjects: [{ deal_score: "desc" }, { _score: "desc" }]
        },
        /*{
          label: "Popular On CarScience",
          value: "advanced.popular_on_carscience",
          searchObjects: [
            {
              "product_performance_rollup.last_365_clicks": "desc",
              _score: "desc"
            }
          ]
        },*/
        {
          label: "Most Searched",
          value: "advanced.most_searched",
          searchObjects: [
            {
              "product_performance_rollup.last_365_impressions": "desc",
              _score: "desc"
            }
          ]
        },
        {
          label: "Recent Price Drops",
          value: "advanced.recent_price_drops",
          searchObjects: [{ "tags.time_created": "desc", _score: "desc" }]
        },
        {
          label: "Biggest Price Drops",
          value: "advanced.biggest_price_drops",
          searchObjects: [
            { "tags.metadata.percent_change": "desc", _score: "desc" }
          ]
        }
      ]
    }
  },
  filter: {
    active: 0,
    modelsActive: getDefaultModelStructure(),
    models: getDefaultModelStructure(),
    modelDefaults: getDefaultModelStructure(),
    options: {
      makeId: [],
      modelId: [],
      modelIdTrim: [],
      makeModelsNested: [],
      condition: [],
      bodyType: [],
      exteriorColor: [],
      interiorColor: [],
      fuelType: [],
      dealScorePhrase: [],
      transmission: [],
      engineType: [],
      drivetrain: [],
      features: [],
      locationRadius: [
        { label: "10 miles", value: "10" },
        { label: "25 miles", value: "25" },
        { label: "50 miles", value: "50" },
        { label: "75 miles", value: "75" },
        { label: "100 miles", value: "100" },
        { label: "150 miles", value: "150" },
        { label: "200 miles", value: "200" },
        { label: "250 miles", value: "250" },
        { label: "500 miles", value: "500" },
        { label: "Nationwide", value: "5000" }
      ],
      year: {
        min: null,
        max: null
      },
      price: {
        min: null,
        max: null
      },
      mileage: {
        min: null,
        max: null
      }
    },
    optionsDefaults: {
      makeId: [],
      modelId: [],
      modelIdTrim: [],
      makeModelsNested: [],
      condition: [],
      bodyType: [],
      exteriorColor: [],
      interiorColor: [],
      fuelType: [],
      dealScorePhrase: [],
      transmission: [],
      engineType: [],
      drivetrain: [],
      features: []
    }
  }
});

// Helper function to check if an OR clause is a known/handleable pattern
const isHandleableOrClause = orClause => {
  // Pattern 1: Trim-specific OR clause (has both model_id and trim with equals)
  if (
    orClause.model_id &&
    orClause.trim &&
    orClause.model_id.equals !== undefined &&
    orClause.trim.equals !== undefined
  ) {
    return true;
  }

  // Pattern 2: Simple model_id equals (model without trim)
  if (
    orClause.model_id &&
    orClause.model_id.equals !== undefined &&
    !orClause.trim
  ) {
    return true;
  }

  // Pattern 3: Simple field with 'in' operator (make_id, model_id, etc.)
  const keys = Object.keys(orClause);
  if (keys.length === 1) {
    const fieldName = keys[0];
    const operators = orClause[fieldName];
    if (
      operators.in !== undefined &&
      ["make_id", "model_id"].includes(fieldName)
    ) {
      return true;
    }
  }

  return false;
};

// Helper function to extract filter clauses (only handling known patterns, tracking unknown ones)
const extractFiltersFromSearchFilters = (
  filters,
  accumulated = {},
  unhandledClauses = []
) => {
  if (!Array.isArray(filters)) return { accumulated, unhandledClauses };

  // Get the list of valid fields from the default model structure
  const validFields = Object.keys(getDefaultModelStructure());

  filters.forEach(filterClause => {
    // Handle OR clauses - only process known patterns
    if (filterClause.or) {
      const handleableItems = [];
      const unhandleableItems = [];

      filterClause.or.forEach(orClause => {
        if (isHandleableOrClause(orClause)) {
          handleableItems.push(orClause);
        } else {
          unhandleableItems.push(orClause);
        }
      });

      // If any items are unhandleable, preserve the entire OR clause
      if (unhandleableItems.length > 0) {
        unhandledClauses.push({ or: filterClause.or });
        return;
      }

      // Process handleable items
      handleableItems.forEach(orClause => {
        // Check if this is a trim clause (has both model_id and trim)
        if (orClause.model_id && orClause.trim) {
          const modelId = orClause.model_id.equals;
          const trimValue = orClause.trim.equals;
          if (modelId && trimValue) {
            // Check if trim field exists in filter models
            if (validFields.includes("trim")) {
              // Add to trim array in "modelId|trim" format
              if (!accumulated.trim) {
                accumulated.trim = [];
              }
              const trimKey = `${modelId}|${trimValue}`;
              if (!accumulated.trim.includes(trimKey)) {
                accumulated.trim.push(trimKey);
              }
            } else {
              // trim field not in filter models, add to unhandled
              unhandleableItems.push(orClause);
            }
          }
        }
        // Process simple fields in the or clause
        else {
          Object.keys(orClause).forEach(fieldName => {
            const operators = orClause[fieldName];
            const camelField = mixins.methods.convertSnakeToCamel(fieldName);

            // Check if field exists in filter models
            if (!validFields.includes(camelField)) {
              unhandleableItems.push(orClause);
              return;
            }

            // Handle 'in' operator
            if (operators.in !== undefined) {
              if (!accumulated[camelField]) {
                accumulated[camelField] = [];
              }
              accumulated[camelField] = [
                ...new Set([...accumulated[camelField], ...operators.in])
              ];
            }
            // Handle 'equals' operator for simple model_id
            else if (
              operators.equals !== undefined &&
              fieldName === "model_id"
            ) {
              if (!accumulated[camelField]) {
                accumulated[camelField] = [];
              }
              if (!accumulated[camelField].includes(operators.equals)) {
                accumulated[camelField].push(operators.equals);
              }
            }
          });
        }
      });

      // If we found unhandleable items, preserve the entire OR clause
      if (unhandleableItems.length > 0) {
        unhandledClauses.push({ or: filterClause.or });
      }
      return;
    }

    // Handle AND clauses - preserve as unhandled (complex logic)
    if (filterClause.and) {
      unhandledClauses.push(filterClause);
      return;
    }

    // Extract simple field filters
    Object.keys(filterClause).forEach(fieldName => {
      const operators = filterClause[fieldName];
      const camelField = mixins.methods.convertSnakeToCamel(fieldName);

      // Check if field exists in filter models
      if (!validFields.includes(camelField)) {
        unhandledClauses.push({ [fieldName]: operators });
        return;
      }

      // Determine if this is a range field
      const rangeFields = ['year', 'price', 'mileage'];
      const isRangeField = rangeFields.includes(camelField);

      // Handle range fields (gte/lte/equals)
      if (operators.gte !== undefined || operators.lte !== undefined) {
        if (!accumulated[camelField]) {
          accumulated[camelField] = { min: null, max: null };
        }
        if (operators.gte !== undefined)
          accumulated[camelField].min = operators.gte;
        if (operators.lte !== undefined)
          accumulated[camelField].max = operators.lte;
      }
      // Handle 'equals' operator on range fields - convert to min/max range
      else if (operators.equals !== undefined && isRangeField) {
        if (!accumulated[camelField]) {
          accumulated[camelField] = { min: null, max: null };
        }
        accumulated[camelField].min = operators.equals;
        accumulated[camelField].max = operators.equals;
      }
      // Handle 'in' operator (arrays)
      else if (operators.in !== undefined) {
        if (!accumulated[camelField]) {
          accumulated[camelField] = [];
        }
        accumulated[camelField] = [
          ...new Set([...accumulated[camelField], ...operators.in])
        ];
      }
      // Handle 'equals' operator for non-range fields
      else if (operators.equals !== undefined) {
        // Features use equals with "1"
        if (fieldName.includes("features.")) {
          accumulated[camelField] = operators.equals;
        } else {
          if (!accumulated[camelField]) {
            accumulated[camelField] = [];
          }
          if (!accumulated[camelField].includes(operators.equals)) {
            accumulated[camelField].push(operators.equals);
          }
        }
      }
    });
  });

  return { accumulated, unhandledClauses };
};

// Helper function to convert models format to searchFilters format
const convertModelsToSearchFilters = models => {
  const filters = [];
  const orFilters = [];
  const trimOrFilters = [];
  const orFilterKeys = ["makeId", "modelId"];

  Object.keys(models).forEach(modelKey => {
    const value = models[modelKey];
    const snakeKey = mixins.methods.convertCamelToSnake(modelKey);

    // Skip empty values
    if (value === null || value === "" || value === undefined) return;
    if (Array.isArray(value) && value.length === 0) return;
    if (
      typeof value === "object" &&
      !Array.isArray(value) &&
      value.min === null &&
      value.max === null
    )
      return;

    // Skip trim, makeId, modelId for now - they're handled specially with OR logic below
    if (["trim", "makeId", "modelId"].includes(modelKey)) return;

    // Handle range fields (year, price, mileage)
    if (
      typeof value === "object" &&
      !Array.isArray(value) &&
      ("min" in value || "max" in value)
    ) {
      const rangeFilter = {};
      if (value.min !== null) rangeFilter.gte = value.min;
      if (value.max !== null) rangeFilter.lte = value.max;
      if (Object.keys(rangeFilter).length > 0) {
        filters.push({ [snakeKey]: rangeFilter });
      }
    }
    // Handle array fields (using 'in' operator)
    else if (Array.isArray(value) && value.length > 0) {
      filters.push({ [snakeKey]: { in: value } });
    }
    // Handle feature fields (using 'equals' with "1")
    else if (modelKey.includes("features.") && value === "1") {
      filters.push({ [snakeKey]: { equals: "1" } });
    }
  });

  // Handle makeId, modelId, and trim with special OR logic
  if (models.makeId && models.makeId.length > 0) {
    orFilters.push({ make_id: { in: models.makeId } });
    if (models.trim && models.trim.length > 0) {
      trimOrFilters.push({ make_id: { in: models.makeId } });
    }
  }

  if (models.modelId && models.modelId.length > 0) {
    if (models.trim && models.trim.length > 0) {
      // For each modelId, check if it has associated trims
      models.modelId.forEach(modelIdValue => {
        const hasTrims = models.trim.some(trimValue =>
          trimValue.includes(modelIdValue + "|")
        );

        if (hasTrims) {
          // Add trim-specific filters for this model
          models.trim.forEach(trimValue => {
            const [trimModelId, trim] = trimValue.split("|");
            if (trimModelId == modelIdValue) {
              trimOrFilters.push({
                model_id: { equals: trimModelId },
                trim: { equals: trim }
              });
            }
          });
        } else {
          // No trims for this model, just add the model filter
          trimOrFilters.push({
            model_id: { equals: modelIdValue }
          });
        }
      });

      orFilters.push({ model_id: { in: models.modelId } });
    } else {
      // No trims selected at all
      orFilters.push({ model_id: { in: models.modelId } });
    }
  }

  // Add OR filters to main filters array
  if (orFilters.length > 0) {
    filters.push({ or: orFilters });
  }
  if (trimOrFilters.length > 0) {
    filters.push({ or: trimOrFilters });
  }

  return filters;
};

// Helper function to update search filter history
const updateSearchFiltersHistory = (searchFilters, state) => {
  // Only update if searchFilters is not empty
  if (!searchFilters || searchFilters.length === 0) {
    return;
  }

  const filtersString = JSON.stringify(searchFilters);

  // Check if this exact filter set already exists in history
  /*const existingIndex = state.searchFiltersHistory.findIndex(
    historyItem => JSON.stringify(historyItem) === filtersString
  );

  // Remove if already exists (we'll add it to the front)
  if (existingIndex !== -1) {
    state.searchFiltersHistory.splice(existingIndex, 1);
  }*/

  // Add to front of history
  state.searchFiltersHistory.unshift(JSON.parse(filtersString));

  // Keep only last 100
  if (state.searchFiltersHistory.length > 100) {
    state.searchFiltersHistory = state.searchFiltersHistory.slice(0, 100);
  }

  // Persist to localStorage
  try {
    window.localStorage.setItem(
      "carscience-visitor-search-filter-history",
      JSON.stringify(state.searchFiltersHistory)
    );
  } catch (e) {
    console.error("Failed to save search filter history:", e);
  }
};

const getters = {
  currentPaginationRangeBlurb: state => condensed => {
    let resultsLow =
      state.pagination.totalResults === 0
        ? 0
        : (state.pagination.currentPage - 1) * state.pagination.perPage + 1;
    let resultsHigh =
      state.pagination.currentPage * state.pagination.perPage >
      state.pagination.totalResults
        ? state.pagination.totalResults
        : state.pagination.currentPage * state.pagination.perPage;
    if (condensed) {
      return (
        mixins.methods.formatNumber(resultsLow) +
        " to " +
        mixins.methods.formatNumber(resultsHigh) +
        " of " +
        mixins.methods.formatNumber(state.pagination.totalResults)
      );
    } else {
      return (
        "Showing " +
        mixins.methods.formatNumber(resultsLow) +
        " to " +
        mixins.methods.formatNumber(resultsHigh) +
        " of " +
        mixins.methods.formatNumber(state.pagination.totalResults) +
        " Vehicles"
      );
    }
  },
  paginationDisablePrevious: state => {
    return state.pagination.currentPage === 1;
  },
  paginationDisableNext: state => {
    return state.pagination.currentPage === state.pagination.totalPages;
  },
  locationSet: state => {
    return (
      state.location.postalCode.length > 0 &&
      state.location.city.length > 0 &&
      state.location.state.length > 0
    );
  },
  locationCityStateFormatted: state => {
    if (state.location.city.length > 0 && state.location.state.length > 0) {
      return state.location.city + ", " + state.location.state;
    }

    return "Nationwide";
  },
  searchLoading: state => {
    return !state.loaded;
  },
  // Now these just return the state directly
  searchFilters: state => {
    return state.searchFilters;
  },
  searchOptions: state => {
    return state.searchOptions;
  },
  searchSorting: state => {
    return state.searchSorting;
  },
  searchScoring: state => {
    return state.searchScoring;
  },
  searchFiltersComplex: state => {
    return state.searchFiltersComplex;
  },
  searchFiltersHistory: state => {
    return state.searchFiltersHistory;
  },
  searchFiltersHistoryByClauseCount: (state, getters, rootState) => {
    const clauseCounts = {};
    const makeIdNameMap = rootState.vehicleMakeModels?.makeIdNameMap || {};
    const modelIdNameMap = rootState.vehicleMakeModels?.modelIdNameMap || {};

    // Helper to recursively extract and count filter clauses
    const processFilterClause = filterClause => {
      // Handle OR clauses
      if (filterClause.or) {
        filterClause.or.forEach(orClause => {
          processFilterClause(orClause);
        });
        return;
      }

      // Handle AND clauses
      if (filterClause.and) {
        filterClause.and.forEach(andClause => {
          processFilterClause(andClause);
        });
        return;
      }

      // Process simple field filters
      Object.keys(filterClause).forEach(fieldName => {
        const operators = filterClause[fieldName];
        let displayField = fieldName;
        let displayValue = null;

        // Replace make_id with "make" and model_id with "model"
        if (fieldName === "make_id") {
          displayField = "make";
        } else if (fieldName === "model_id") {
          displayField = "model";
        }

        // Build clause key based on operator type
        if (operators.equals !== undefined) {
          // Handle equals operator
          if (fieldName === "make_id") {
            displayValue = makeIdNameMap[operators.equals] || operators.equals;
          } else if (fieldName === "model_id") {
            displayValue = modelIdNameMap[operators.equals] || operators.equals;
          } else {
            displayValue = operators.equals;
          }
          const clauseKey = `${displayField}|${displayValue}`;
          clauseCounts[clauseKey] = (clauseCounts[clauseKey] || 0) + 1;
        } else if (operators.not_equals !== undefined) {
          // Handle equals operator
          if (fieldName === "make_id") {
            displayValue =
              makeIdNameMap[operators.not_equals] || operators.not_equals;
          } else if (fieldName === "model_id") {
            displayValue =
              modelIdNameMap[operators.not_equals] || operators.not_equals;
          } else {
            displayValue = operators.not_equals;
          }
          const clauseKey = `${displayField}|not_${displayValue}`;
          clauseCounts[clauseKey] = (clauseCounts[clauseKey] || 0) + 1;
        } else if (operators.in !== undefined && Array.isArray(operators.in)) {
          // Handle 'in' operator - count each value separately
          operators.in.forEach(value => {
            if (fieldName === "make_id") {
              displayValue = makeIdNameMap[value] || value;
            } else if (fieldName === "model_id") {
              displayValue = modelIdNameMap[value] || value;
            } else {
              displayValue = value;
            }
            const clauseKey = `${displayField}|${displayValue}`;
            clauseCounts[clauseKey] = (clauseCounts[clauseKey] || 0) + 1;
          });
        } else if (
          operators.not_in !== undefined &&
          Array.isArray(operators.not_in)
        ) {
          // Handle 'in' operator - count each value separately
          operators.not_in.forEach(value => {
            if (fieldName === "make_id") {
              displayValue = makeIdNameMap[value] || value;
            } else if (fieldName === "model_id") {
              displayValue = modelIdNameMap[value] || value;
            } else {
              displayValue = value;
            }
            const clauseKey = `${displayField}|not_${displayValue}`;
            clauseCounts[clauseKey] = (clauseCounts[clauseKey] || 0) + 1;
          });
        } else if (operators.gte !== undefined || operators.lte !== undefined) {
          // Handle range operators
          const rangeKey = [];
          if (operators.gte !== undefined) {
            rangeKey.push(`gte:${operators.gte}`);
          }
          if (operators.lte !== undefined) {
            rangeKey.push(`lte:${operators.lte}`);
          }
          const clauseKey = `${displayField}|${rangeKey.join(",")}`;
          clauseCounts[clauseKey] = (clauseCounts[clauseKey] || 0) + 1;
        }
      });
    };

    // Iterate through all history entries
    state.searchFiltersHistory.forEach(historyEntry => {
      if (Array.isArray(historyEntry)) {
        historyEntry.forEach(filterClause => {
          processFilterClause(filterClause);
        });
      }
    });

    return clauseCounts;
  },
  // Derive modelsActive from searchFilters
  derivedModelsActive: state => {
    const { accumulated } = extractFiltersFromSearchFilters(
      state.searchFilters,
      getDefaultModelStructure(),
      []
    );
    return accumulated;
  }
};

const actions = {
  [CHECK_FILTER_MODELS_UPDATED]: ({ commit, dispatch }) => {
    return new Promise((resolve, reject) => {
      if (
        JSON.stringify(state.filter.modelsActive) ===
        JSON.stringify(state.filter.models)
      ) {
        resolve(false);
      } else {
        resolve(true);
      }
    });
  },
  [CHECK_FILTER_MODELS_DEFAULTED]: ({ commit, dispatch }) => {
    return new Promise((resolve, reject) => {
      if (
        JSON.stringify(state.filter.modelDefaults) ===
        JSON.stringify(state.filter.models)
      ) {
        resolve(true);
      } else {
        resolve(false);
      }
    });
  },
  [POPULATE_FILTER_OPTIONS]: (
    { commit, dispatch },
    { filterParams = {}, includeLocationParam = true, featuresOnly = false }
  ) => {
    return new Promise((resolve, reject) => {
      let facetingFields = {
        condition: [],
        make_id: [],
        model_id: [],
        body_type: [],
        exterior_color: [],
        interior_color: [],
        fuel_type: [],
        engine_type: [],
        deal_score_phrase: [],
        deal_score: ["range"],
        transmission: [],
        drivetrain: [],
        price: ["range"],
        mileage: ["range"],
        year: ["range"]
      };

      if (featuresOnly) {
        commit(SET_FEATURES_FILTER_OPTIONS_LOADING, true);

        facetingFields = {
          "features.fog_lights": ["nested_terms"],
          "features.autopilot": ["nested_terms"],
          "features.traction_control": ["nested_terms"],
          "features.keyless_entry": ["nested_terms"],
          "features.sunshades": ["nested_terms"],
          "features.backup_camera": ["nested_terms"],
          "features.blind_spot_monitor": ["nested_terms"],
          "features.stability_control": ["nested_terms"],
          "features.cruise_control": ["nested_terms"],
          "features.automatic_parking": ["nested_terms"],
          "features.bluetooth_connectivity": ["nested_terms"],
          "features.driver_attention_warning": ["nested_terms"],
          "features.offroad_driving_assist": ["nested_terms"],
          "features.apple_carplay": ["nested_terms"],
          "features.android_auto": ["nested_terms"],
          "features.navigation_system": ["nested_terms"],
          "features.heads_up_display": ["nested_terms"],
          "features.adaptive_cruise_control": ["nested_terms"],
          "features.trailer_hitch": ["nested_terms"],
          "features.360_degree_camera": ["nested_terms"],
          "features.collision_warning": ["nested_terms"],
          "features.touchscreen_display": ["nested_terms"],
          "features.ventilated_seats": ["nested_terms"],
          "features.adaptive_headlights": ["nested_terms"],
          "features.roof_rails": ["nested_terms"],
          "features.dash_camera": ["nested_terms"],
          "features.sunroof_moonroof": ["nested_terms"],
          "features.lane_keep_assist": ["nested_terms"],
          "features.power_trunk": ["nested_terms"],
          "features.climate_control": ["nested_terms"],
          "features.cross_traffic_alert": ["nested_terms"],
          "features.automatic_emergency_braking": ["nested_terms"],
          "features.traffic_sign_recognition": ["nested_terms"],
          "features.homelink": ["nested_terms"],
          "features.rain_sensing_wipers": ["nested_terms"],
          "features.voice_controls": ["nested_terms"],
          "features.anti_theft_sensors": ["nested_terms"],
          "features.power_adjustable_seats": ["nested_terms"],
          "features.heated_mirrors": ["nested_terms"],
          "features.memory_seats": ["nested_terms"],
          "features.push_button_start": ["nested_terms"],
          "features.power_folding_mirrors": ["nested_terms"],
          "features.heated_steering_wheel": ["nested_terms"],
          "features.illuminated_entry": ["nested_terms"],
          "features.brake_assist": ["nested_terms"],
          "features.cd_player": ["nested_terms"],
          "features.usb_inputs": ["nested_terms"],
          "features.remote_start": ["nested_terms"],
          "features.lane_departure_warning": ["nested_terms"],
          "features.premium_wheels": ["nested_terms"],
          "features.parking_sensors": ["nested_terms"],
          "features.premium_sound_system": ["nested_terms"],
          "features.leather_seats": ["nested_terms"],
          "features.led_headlights": ["nested_terms"],
          "features.memory_mirrors": ["nested_terms"],
          "features.satellite_radio": ["nested_terms"],
          "features.digital_odometer": ["nested_terms"],
          "features.wifi_hotspot": ["nested_terms"],
          "features.hands_free_trunk": ["nested_terms"],
          "features.wireless_phone_charging": ["nested_terms"],
          "features.hands_free_phone": ["nested_terms"],
          "features.third_row_seating": ["nested_terms"],
          "features.heated_seats": ["nested_terms"]
        };
      }

      let searchParams = {
        filters: filterParams,
        options: {
          deduplicate_field: ["vin"],
          faceting: {
            fields: facetingFields,
            size: 2000
          }
        },
        pagination: {
          page: 1,
          per_page: 1
        }
      };

      if (includeLocationParam) {
        searchParams.options.location_search = {
          postal_code: state.location.postalCode,
          radius: state.location.radius,
          include_distance_field: true
          // use_payable_radius: true
        };
      }

      axios
        .post(
          "https://api-v2.jumpfeed.com/network/publisher/products/vehicles",
          searchParams
        )
        .then(resp => {
          let esSearchFacets = resp.data.metadata.facets;
          if (esSearchFacets) {
            commit(SET_FILTER_OPTIONS, esSearchFacets);
          }
          resolve();
        })
        .catch(err => {
          console.log(err);
          reject(err);
        })
        .finally(() => {
          commit(SET_FEATURES_FILTER_OPTIONS_LOADING, false);
        });
    });
  },
  // New action to apply filter.models to searchFilters
  [APPLY_FILTER_MODELS]: ({ commit, state }) => {
    const filters = convertModelsToSearchFilters(state.filter.models);
    // Merge in any complex/unhandled filter clauses
    const allFilters = [...filters, ...state.searchFiltersComplex];
    commit(SET_SEARCH_FILTERS, allFilters);
  },
  [REQUEST_NEW_VISITOR_SEARCH_SCORING]: (
    { commit, state },
    { conversationHistory, message, visitorProfile }
  ) => {
    return new Promise((resolve, reject) => {
      if (state.pendingScoringRequests.length > 0) {
        state.pendingScoringRequests.forEach(controller => {
          controller.abort();
        });
        state.pendingScoringRequests = [];
      }
      let scoringController = new AbortController();
      state.pendingScoringRequests.push(scoringController);

      axios({
        url:
          "https://api-v2.jumpfeed.com/network/publisher/carscience-search-ai-scoring-v2",
        method: "POST",
        signal: scoringController.signal,
        data: {
          conversation_history: conversationHistory,
          message: message.substring(0, 500), // trim to prevent potential mass inputs
          visitor_profile: visitorProfile
        }
      })
        .then(response => {
          let output = response.data.data;

          let thisSearchScoring =
            "scoring" in output.reply.scoring_object
              ? output.reply.scoring_object.scoring
              : [];

          commit(SET_SEARCH_SCORING, thisSearchScoring);
          resolve(thisSearchScoring);
        })
        .catch(e => {
          console.error(e);
          reject(e);
        });
    });
  }
};

const mutations = {
  [SET_STATE_FROM_QUERY_PARAM](state, queryParam) {
    let existingUrlParamsState = null;
    try {
      existingUrlParamsState = JSON.parse(
        LZString.decompressFromEncodedURIComponent(queryParam)
      );
      if (
        existingUrlParamsState == null ||
        typeof existingUrlParamsState !== "object"
      ) {
        existingUrlParamsState = null;
      }
    } catch (e) {
      existingUrlParamsState = null;
    }
    if (existingUrlParamsState !== null) {
      // New format: supports searchFilters, searchOptions, searchSorting
      if ("searchFilters" in existingUrlParamsState) {
        state.searchFilters = JSON.parse(
          JSON.stringify(existingUrlParamsState.searchFilters)
        );
        if ("searchOptions" in existingUrlParamsState) {
          state.searchOptions = JSON.parse(
            JSON.stringify(existingUrlParamsState.searchOptions)
          );
        }
        if ("searchSorting" in existingUrlParamsState) {
          state.searchSorting = JSON.parse(
            JSON.stringify(existingUrlParamsState.searchSorting)
          );
        }
        // Sync derived state and track complex clauses
        const {
          accumulated: derived,
          unhandledClauses
        } = extractFiltersFromSearchFilters(
          state.searchFilters,
          getDefaultModelStructure(),
          []
        );
        state.filter.modelsActive = derived;
        state.filter.models = JSON.parse(JSON.stringify(derived));
        state.searchFiltersComplex = unhandledClauses;
      }
      // Legacy format: supports old filter.modelsActive structure
      else if (
        "pagination" in existingUrlParamsState &&
        "location" in existingUrlParamsState &&
        "sort" in existingUrlParamsState &&
        "filter" in existingUrlParamsState &&
        "keyword" in existingUrlParamsState
      ) {
        state.filter.models = JSON.parse(
          JSON.stringify(existingUrlParamsState.filter.modelsActive)
        );
        state.filter.modelsActive = JSON.parse(
          JSON.stringify(existingUrlParamsState.filter.modelsActive)
        );
        // Convert to new searchFilters format - this will be implemented below
        // For now, keep backward compatibility
      }

      // Common fields
      if ("location" in existingUrlParamsState) {
        state.location = JSON.parse(
          JSON.stringify(existingUrlParamsState.location)
        );
      }
      if ("pagination" in existingUrlParamsState) {
        state.pagination = JSON.parse(
          JSON.stringify(existingUrlParamsState.pagination)
        );
      }
      if (
        "sort" in existingUrlParamsState &&
        "model" in existingUrlParamsState.sort
      ) {
        state.sort.model = JSON.parse(
          JSON.stringify(existingUrlParamsState.sort.model)
        );
      }
    }
  },
  [SET_LOADING]: state => {
    state.loaded = false;
    state.pagination.disableAll = true;
  },
  [SET_FEATURES_FILTER_OPTIONS_LOADING]: (state, loadingStatus) => {
    state.featuresFilterOptionsLoading = loadingStatus;
  },
  [SET_LOADED]: state => {
    state.loaded = true;
    state.pagination.disableAll = false;
  },
  [SET_URL_PARAMS]: state => {
    let urlParams = new URLSearchParams(window.location.search);
    urlParams.set(
      "query",
      LZString.compressToEncodedURIComponent(
        JSON.stringify({
          searchFilters: state.searchFilters,
          searchOptions: state.searchOptions,
          searchSorting: state.searchSorting,
          location: state.location,
          pagination: state.pagination,
          sort: {
            model: state.sort.model
          }
        })
      )
    );

    window.history.replaceState(
      history.state,
      null,
      "?" + urlParams.toString()
    );
  },
  [SET_PAGE]: (state, page) => {
    if (
      page === "next" &&
      state.pagination.currentPage < state.pagination.totalPages
    ) {
      state.pagination.currentPage++;
    } else if (page === "last") {
      state.pagination.currentPage = state.pagination.totalPages;
    } else if (page === "previous" && state.pagination.currentPage > 1) {
      state.pagination.currentPage--;
    } else if (page === "first") {
      state.pagination.currentPage = 1;
    } else if (typeof page === "number") {
      state.pagination.currentPage = page;
    }
    // Note: disableNext and disablePrevious are now computed getters
  },
  [SET_PAGINATION_TOTALS]: (state, totalsData) => {
    state.pagination.totalResults = totalsData.results;
    state.pagination.totalPages =
      totalsData.pages > 500 ? 500 : totalsData.pages;
  },
  [SET_FILTER_OPTIONS](state, options = {}) {
    let colorMap = {
      Black: "#000",
      White: "#FFF",
      Gray: "#8A8A8A",
      Silver:
        "linear-gradient(90deg, rgba(177,177,177,0.9) 0%, rgba(205,205,205,0.9) 50%, rgba(237,237,237,0.7) 100%)",
      Red: "#DE1616",
      Orange: "#FFA500",
      Brown: "",
      Gold:
        "linear-gradient(90deg, rgba(184,161,45,0.85) 0%, rgba(235,210,83,1) 50%, rgba(228,217,151,0.5) 100%)",
      Yellow: "#FEE12B",
      Green: "#1D9A1D",
      Blue: "#1F57C3",
      Purple: "#800080",
      Other: "#6F6F6F34"
    };

    let constructOption = (option, optionKey) => {
      let optionLabel = option.label ?? option.key;
      let optionValue = option.value ?? option.key;

      if (optionKey === "makeId" || optionKey === "make_id") {
        optionLabel = this.state.vehicleMakeModels.makeIdNameMap[optionValue];
      } else if (optionKey === "modelId" || optionKey == "model_id") {
        optionLabel = this.state.vehicleMakeModels.modelIdNameMap[optionValue];
      }

      if (
        optionKey === "exteriorColor" ||
        optionKey === "exterior_color" ||
        optionKey === "interiorColor" ||
        optionKey === "interior_color"
      ) {
        if (optionValue === "Other") {
          optionLabel = "Other/Unknown";
        }
      }

      let thisOption = {
        label: optionLabel,
        value: optionValue,
        count: mixins.methods.formatNumber(option.doc_count ?? "")
      };
      if (
        optionKey === "exteriorColor" ||
        optionKey === "exterior_color" ||
        optionKey === "interiorColor" ||
        optionKey === "interior_color"
      ) {
        thisOption.color = colorMap[thisOption.value];
      }

      return thisOption;
    };

    // Assign our standard options values (and accept ES facets formatting)'
    let featuresOptions = [];
    Object.keys(options).forEach(optionKey => {
      if (
        ["price", "year", "mileage", "dealScore"].includes(
          mixins.methods.convertSnakeToCamel(optionKey)
        )
      ) {
        let min =
          options[optionKey].find(option => option.key === "min")?.doc_count ||
          0;
        let max =
          options[optionKey].find(option => option.key === "max")?.doc_count ||
          0;
        let avg =
          options[optionKey].find(option => option.key === "avg")?.doc_count ||
          null;

        // Store the avg value in searchMarketAverages
        if (avg !== null) {
          state.searchMarketAverages[
            mixins.methods.convertSnakeToCamel(optionKey)
          ] = avg;
        }

        if (optionKey !== "year" && optionKey !== "deal_score") {
          min = Math.floor(min / 1000) * 1000;
          max = Math.ceil(max / 1000) * 1000;
          if (optionKey == "mileage" && max > 200000) max = 200000;
          if (optionKey == "mileage" && min > 199000) min = 199000;
          if (optionKey == "price" && max > 150000) max = 150000;
          if (optionKey == "price" && min > 149000) min = 149000;
        }

        if (optionKey !== "deal_score") {
          state.filter.options[
            mixins.methods.convertSnakeToCamel(optionKey)
          ] = {
            min: min,
            max: max
          };
        }
      } else if (optionKey == "model_id_trim") {
        let thisOptions = {};
        options[optionKey].forEach(option => {
          let thisOption = constructOption(option, optionKey);
          let [thisModelId, thisTrim] = thisOption.value.split("|");
          if (!(thisModelId in thisOptions)) {
            thisOptions[thisModelId] = [];
          }
          thisOptions[thisModelId].push({
            label: thisTrim,
            value: thisOption.value,
            count: thisOption.count
          });
          thisOptions[thisModelId].sort((a, b) =>
            a.label.localeCompare(b.label, "en", { sensitivity: "base" })
          );
        });
        state.filter.options[
          mixins.methods.convertSnakeToCamel(optionKey)
        ] = thisOptions;
      } else if (optionKey.includes("features.")) {
        options[optionKey].forEach(option => {
          let thisOption = constructOption(option, optionKey);
          if (thisOption.value == 0) return;

          let thisFeatureField = optionKey.split(".")[1];
          let thisFeatureFieldCleaned = mixins.methods
            .convertSnakeToCamel(thisFeatureField)
            .replace(/([A-Z])/g, " $1");
          let thisFeatureLabel =
            thisFeatureFieldCleaned.charAt(0).toUpperCase() +
            thisFeatureFieldCleaned.slice(1);

          featuresOptions.push({
            label: thisFeatureLabel,
            value: thisFeatureLabel,
            count: thisOption.count,
            key: mixins.methods.convertSnakeToCamel(optionKey)
          });
        });
        featuresOptions.sort((a, b) =>
          a.label.localeCompare(b.label, "en", { sensitivity: "base" })
        );
      } else {
        let thisOptions = [];
        let activeModelsNotEncountered = [];
        if (
          mixins.methods.convertSnakeToCamel(optionKey) in
          state.filter.modelsActive
        ) {
          activeModelsNotEncountered = state.filter.modelsActive[
            mixins.methods.convertSnakeToCamel(optionKey)
          ].slice(0);
        }

        options[optionKey].forEach(option => {
          let thisOption = constructOption(option, optionKey);
          thisOptions.push(thisOption);
          if (activeModelsNotEncountered.includes(thisOption.value)) {
            activeModelsNotEncountered = activeModelsNotEncountered.filter(
              value => value != thisOption.value
            );
          }
        });

        // If we still have any un-encountered active models, add them to prevent issues
        if (activeModelsNotEncountered.length > 0) {
          activeModelsNotEncountered.forEach(activeModelValue => {
            thisOptions.push(
              constructOption(
                { key: activeModelValue, doc_count: 0 },
                optionKey
              )
            );
          });
        }

        // Custom sort for dealScorePhrase
        if (
          optionKey === "dealScorePhrase" ||
          optionKey === "deal_score_phrase"
        ) {
          const dealScoreOrder = ["excellent", "great", "good", "fair", "poor"];
          state.filter.options[
            mixins.methods.convertSnakeToCamel(optionKey)
          ] = thisOptions.sort((a, b) => {
            const aIndex = dealScoreOrder.indexOf(a.label.toLowerCase());
            const bIndex = dealScoreOrder.indexOf(b.label.toLowerCase());
            return aIndex - bIndex;
          });
        } else {
          state.filter.options[
            mixins.methods.convertSnakeToCamel(optionKey)
          ] = thisOptions.sort((a, b) =>
            a.label.localeCompare(b.label, "en", { sensitivity: "base" })
          );
        }

        if (optionKey === "makeId" || optionKey === "make_id") {
          state.filter.options.makeModelsNested = thisOptions;
        }
      }
    });

    if (featuresOptions.length > 0) {
      state.filter.options.features = featuresOptions;
    }

    // Set up our custom "makeModelsNested" option
    if (Object.keys(options).includes("model_id")) {
      let makeModelsNestedIndexMap = {};
      state.filter.options.modelId.forEach(modelOption => {
        let thisMakeId = this.state.vehicleMakeModels.modelIdMakeIdMap[
          modelOption.value
        ];
        if (!thisMakeId) return;

        if (!([thisMakeId] in makeModelsNestedIndexMap)) {
          makeModelsNestedIndexMap[
            thisMakeId
          ] = state.filter.options.makeModelsNested.findIndex(makeOption => {
            return makeOption.value == thisMakeId;
          });
          state.filter.options.makeModelsNested[
            makeModelsNestedIndexMap[thisMakeId]
          ].modelOptions = [];
        }

        state.filter.options.makeModelsNested[
          makeModelsNestedIndexMap[thisMakeId]
        ].modelOptions.push(modelOption);
      });
    }
  },
  [SET_SORT]: (state, sortVal) => {
    state.sort.model = sortVal;
  },
  [SET_LOCATION]: (state, locationObject) => {
    const US_STATES = [
      "AL",
      "AK",
      "AS",
      "AZ",
      "AR",
      "CA",
      "CO",
      "CT",
      "DE",
      "DC",
      "FM",
      "FL",
      "GA",
      "GU",
      "HI",
      "ID",
      "IL",
      "IN",
      "IA",
      "KS",
      "KY",
      "LA",
      "ME",
      "MH",
      "MD",
      "MA",
      "MI",
      "MN",
      "MS",
      "MO",
      "MT",
      "NE",
      "NV",
      "NH",
      "NJ",
      "NM",
      "NY",
      "NC",
      "ND",
      "MP",
      "OH",
      "OK",
      "OR",
      "PW",
      "PA",
      "PR",
      "RI",
      "SC",
      "SD",
      "TN",
      "TX",
      "UT",
      "VT",
      "VI",
      "VA",
      "WA",
      "WV",
      "WI",
      "WY",
      "AE",
      "AA",
      "AP"
    ];

    // Validate and normalize location object
    const normalized = {
      city: typeof locationObject.city === "string" ? locationObject.city : "",
      state:
        typeof locationObject.state === "string" ? locationObject.state : "",
      postalCode:
        typeof locationObject.postalCode === "string" &&
        locationObject.postalCode.length === 5
          ? locationObject.postalCode
          : "",
      radius:
        locationObject.radius >= 10 && locationObject.radius <= 5000
          ? locationObject.radius
          : 100
    };

    // If outside US, clear location
    if (normalized.state && !US_STATES.includes(normalized.state)) {
      normalized.city = "";
      normalized.state = "";
      normalized.postalCode = "";
    }

    // Update state
    state.location = normalized;

    // Update searchOptions.location_search to stay in sync
    state.searchOptions.location_search.postal_code = normalized.postalCode;
    state.searchOptions.location_search.radius = normalized.radius;

    // Persist to localStorage
    const storageKey = window.location.hostname;
    if (normalized.postalCode) {
      window.localStorage.setItem(
        `${storageKey}-searchLocationPostalCode`,
        normalized.postalCode
      );
    }
    if (normalized.city) {
      window.localStorage.setItem(
        `${storageKey}-searchLocationCity`,
        normalized.city
      );
    }
    if (normalized.state) {
      window.localStorage.setItem(
        `${storageKey}-searchLocationState`,
        normalized.state
      );
    }
    if (normalized.radius >= 10 && normalized.radius <= 5000) {
      window.localStorage.setItem(
        `${storageKey}-searchLocationRadius`,
        normalized.radius
      );
    }
  },
  [RESET_PAGINATION]: state => {
    state.pagination.totalResults = 0;
    state.pagination.currentPage = 1;
    state.pagination.totalPages = 500;
    // Note: disableNext and disablePrevious are now computed getters
  },
  // New mutations for direct search object manipulation
  [SET_SEARCH_FILTERS](state, filters) {
    state.searchFilters = JSON.parse(JSON.stringify(filters));
    // Sync standardized state and track complex clauses
    const {
      accumulated: derived,
      unhandledClauses
    } = extractFiltersFromSearchFilters(
      state.searchFilters,
      getDefaultModelStructure(),
      []
    );
    state.filter.modelsActive = derived;
    state.filter.models = JSON.parse(JSON.stringify(derived));
    state.searchFiltersComplex = unhandledClauses;
    // Update history
    updateSearchFiltersHistory(state.searchFilters, state);
  },
  [ADD_SEARCH_FILTER](state, filterClause) {
    state.searchFilters.push(filterClause);
    // Re-sync standardized state and track complex clauses
    const {
      accumulated: derived,
      unhandledClauses
    } = extractFiltersFromSearchFilters(
      state.searchFilters,
      getDefaultModelStructure(),
      []
    );
    state.filter.modelsActive = derived;
    state.filter.models = JSON.parse(JSON.stringify(derived));
    state.searchFiltersComplex = unhandledClauses;
    // Update history
    updateSearchFiltersHistory(state.searchFilters, state);
  },
  [REMOVE_SEARCH_FILTER](state, index) {
    state.searchFilters.splice(index, 1);
    // Re-sync standardized state
    const {
      accumulated: derived,
      unhandledClauses
    } = extractFiltersFromSearchFilters(
      state.searchFilters,
      getDefaultModelStructure(),
      []
    );
    state.filter.modelsActive = derived;
    state.filter.models = JSON.parse(JSON.stringify(derived));
    state.searchFiltersComplex = unhandledClauses;
    // Update history
    updateSearchFiltersHistory(state.searchFilters, state);
  },
  [UPDATE_SEARCH_FILTER](state, { index, filterClause }) {
    state.searchFilters[index] = filterClause;
    // Re-sync standardized state
    const {
      accumulated: derived,
      unhandledClauses
    } = extractFiltersFromSearchFilters(
      state.searchFilters,
      getDefaultModelStructure(),
      []
    );
    state.filter.modelsActive = derived;
    state.filter.models = JSON.parse(JSON.stringify(derived));
    state.searchFiltersComplex = unhandledClauses;
    // Update history
    updateSearchFiltersHistory(state.searchFilters, state);
  },
  [SET_SEARCH_OPTIONS]: (state, options) => {
    state.searchOptions = JSON.parse(JSON.stringify(options));
    // Sync location data from options to state.location (for UI display)
    if (options.location_search) {
      state.location.postalCode = options.location_search.postal_code || "";
      state.location.radius = options.location_search.radius || 100;
      // Note: city and state are not in searchOptions, they're UI-only
    }
  },
  [SET_SEARCH_SORTING]: (state, sorting) => {
    state.searchSorting = JSON.parse(JSON.stringify(sorting));
    // Try to find matching sort model
    let foundSortModel = null;
    Object.keys(state.sort.options).forEach(sortType => {
      state.sort.options[sortType].forEach(sortOption => {
        if (
          JSON.stringify(sortOption.searchObjects.filter(o => Object.keys(o)[0] !== '_score')) === JSON.stringify(sorting.filter(o => Object.keys(o)[0] !== '_score'))
        ) {
          foundSortModel = sortOption.value;
        }
      });
    });
    if (foundSortModel) {
      state.sort.model = foundSortModel;
    }
  },
  [SYNC_STANDARDIZED_STATE](state) {
    // Re-sync all standardized state from search objects
    const {
      accumulated: derived,
      unhandledClauses
    } = extractFiltersFromSearchFilters(
      state.searchFilters,
      getDefaultModelStructure(),
      []
    );
    state.filter.modelsActive = derived;
    state.filter.models = JSON.parse(JSON.stringify(derived));
    state.searchFiltersComplex = unhandledClauses;
  },
  // Scoring mutations
  [SET_SEARCH_SCORING]: (state, scoring) => {
    state.searchScoring = JSON.parse(JSON.stringify(scoring));
    window.localStorage.setItem(
      "carscience-visitor-profile-scoring",
      JSON.stringify(scoring)
    );
  },
  [ADD_SEARCH_SCORING]: (state, scoringClause) => {
    state.searchScoring.push(scoringClause);
  },
  [REMOVE_SEARCH_SCORING]: (state, index) => {
    state.searchScoring.splice(index, 1);
  },
  [UPDATE_SEARCH_SCORING]: (state, { index, scoringClause }) => {
    state.searchScoring[index] = scoringClause;
  },
  [REMOVE_COMPLEX_FILTERS]: state => {
    // Clear the complex filters array
    state.searchFiltersComplex = [];
    // Re-apply filter models to regenerate searchFilters without complex filters
    const filters = convertModelsToSearchFilters(state.filter.models);
    state.searchFilters = JSON.parse(JSON.stringify(filters));
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
